"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HomeAgentSignature } from "~/components/siteglue/home/home-agent-signature";
import { publishedAgentsForHomeHero } from "~/lib/siteglue/home-hero-published-agents";
import { SITEGLUE_CTA_CONFIG, trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import { SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import type { MoonlightAgentListItem } from "~/lib/marketplace/moonlight-home";
import { api } from "~/trpc/react";
import { cn } from "~/lib/utils";

const ROTATE_MS = 6000;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

type HomeHeroEmployeeRotatorProps = {
  /** Analytics event when a card is clicked (defaults to hero_employee_card_clicked). */
  analyticsClickEvent?: string;
  /** Show the “Example AI Employee” eyebrow above the industry label. */
  showExampleLabel?: boolean;
  /** Filter the 3–6 examples by role instead of channel. */
  enableRoleFilter?: boolean;
};

export function HomeHeroEmployeeRotator({
  analyticsClickEvent = "hero_employee_card_clicked",
  showExampleLabel = true,
  enableRoleFilter = false,
}: HomeHeroEmployeeRotatorProps = {}) {
  const agentsQuery = api.project.getStaffingAgents.useQuery({
    listingType: "ALL",
  });
  const [role, setRole] = useState("All");

  const examples = useMemo(() => {
    const raw = (agentsQuery.data?.agents ?? []) as MoonlightAgentListItem[];
    const published = publishedAgentsForHomeHero(raw).slice(0, 6);
    if (!enableRoleFilter || role === "All") return published;
    return published.filter((card) => card.role === role);
  }, [agentsQuery.data?.agents, enableRoleFilter, role]);

  const roleOptions = useMemo(() => {
    const raw = (agentsQuery.data?.agents ?? []) as MoonlightAgentListItem[];
    const roles = publishedAgentsForHomeHero(raw)
      .slice(0, 6)
      .map((card) => card.role);
    return ["All", ...Array.from(new Set(roles))];
  }, [agentsQuery.data?.agents]);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const viewedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (index >= examples.length) setIndex(0);
  }, [examples.length, index]);

  const current = examples[index] ?? examples[0];
  const slideLabel = current
    ? `${current.agentName} · ${current.role} · ${current.companyName}`
    : "AI Employees";

  useEffect(() => {
    if (!current) return;
    if (viewedRef.current.has(current.id)) return;
    viewedRef.current.add(current.id);
    trackSiteGlueEvent("hero_employee_card_viewed", {
      index,
      industry: current.industryLabel,
      role: current.role,
      agent_name: current.agentName,
      agent_id: current.id,
      interaction: "auto_or_mount",
    });
  }, [index, current]);

  useEffect(() => {
    if (reducedMotion || paused || examples.length < 2) return;
    const id = window.setInterval(() => {
      setIndex((i) => {
        const next = (i + 1) % examples.length;
        trackSiteGlueEvent("hero_employee_card_advanced", {
          index: next,
          industry: examples[next]?.industryLabel,
          role: examples[next]?.role,
          agent_id: examples[next]?.id,
          interaction: "auto",
        });
        return next;
      });
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [reducedMotion, paused, examples]);

  const go = (delta: number, interaction: "prev" | "next") => {
    if (examples.length < 2) return;
    setIndex((i) => {
      const next = (i + delta + examples.length) % examples.length;
      trackSiteGlueEvent("hero_employee_card_advanced", {
        index: next,
        industry: examples[next]?.industryLabel,
        role: examples[next]?.role,
        agent_id: examples[next]?.id,
        interaction,
      });
      return next;
    });
  };

  if (agentsQuery.isLoading) {
    return (
      <div className="relative mx-auto w-full max-w-md space-y-3 lg:max-w-none">
        <div className="h-4 w-20 animate-pulse rounded bg-white/10" />
        <div className="min-h-[22rem] animate-pulse rounded-2xl border border-white/10 bg-white/5" />
      </div>
    );
  }

  if (!current || examples.length === 0) {
    return (
      <div className="relative mx-auto w-full max-w-md space-y-3 rounded-2xl border border-white/10 bg-white/5 p-8 text-center lg:max-w-none">
        {enableRoleFilter ? (
          <div className="flex flex-wrap justify-center gap-2">
            {roleOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setRole(option)}
                className="rounded-full border border-white/20 px-3 py-1 text-xs text-white"
              >
                {option}
              </button>
            ))}
          </div>
        ) : null}
        <p className="text-sm text-white/70">Live AI employees are loading soon.</p>
        <Link
          href="/agents"
          className="mt-4 inline-flex text-sm font-semibold text-[#9cbb5a] underline-offset-4 hover:underline"
        >
          Browse AI Employees
        </Link>
      </div>
    );
  }

  return (
    <div
      className="relative mx-auto w-full max-w-md space-y-3 lg:max-w-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      {enableRoleFilter ? (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter examples by role">
          {roleOptions.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={role === option}
              onClick={() => {
                setRole(option);
                setIndex(0);
              }}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium",
                role === option
                  ? "border-[#9cbb5a] bg-[#9cbb5a] text-slate-950"
                  : "border-white/20 text-white/80 hover:bg-white/10",
              )}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          {showExampleLabel ? (
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55">
              Example AI Employee
            </p>
          ) : null}
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9cbb5a]">
            {current.industryLabel}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous AI Employee"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9cbb5a]"
            onClick={() => go(-1, "prev")}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Next AI Employee"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9cbb5a]"
            onClick={() => go(1, "next")}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      <div
        className="relative min-h-[22rem]"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="sr-only">{slideLabel}</span>
        {examples.map((example, i) => {
          const isActive = i === index;
          return (
            <div
              key={example.id}
              className={cn(
                "absolute inset-0 transition-opacity duration-500",
                reducedMotion ? "duration-0" : "",
                isActive ? "z-10 opacity-100" : "z-0 pointer-events-none opacity-0",
              )}
              aria-hidden={!isActive}
            >
              <Link
                href={example.profileHref}
                className="block rounded-2xl transition-opacity hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9cbb5a]"
                aria-label={`${SITEGLUE_CTA_CONFIG.launchLiveDemo.label} — ${example.agentName}, ${example.role}`}
                onClick={() =>
                  trackSiteGlueEvent(analyticsClickEvent, {
                    index: i,
                    industry: example.industryLabel,
                    role: example.role,
                    agent_id: example.id,
                    agent_name: example.agentName,
                    destination: example.profileHref,
                  })
                }
              >
                <HomeAgentSignature
                  tone="dark"
                  agentName={example.agentName}
                  agentTitle={example.role}
                  companyName={example.companyName}
                  avatarSrc={example.avatarSrc}
                  accentColor={SITEGLUE_COLORS.blue}
                  phoneDisplay={example.phoneDisplay}
                  emailAddress={example.emailAddress}
                  websiteDisplay={example.websiteDisplay}
                  activeChannelIds={example.activeChannelIds}
                  industryLabel={example.industryLabel}
                  responsibilities={example.responsibilities}
                />
              </Link>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center gap-1.5" aria-hidden>
        {examples.map((example, i) => (
          <span
            key={example.id}
            className={cn(
              "h-1.5 w-1.5 rounded-full transition-colors",
              i === index ? "bg-[#9cbb5a]" : "bg-white/25",
            )}
          />
        ))}
      </div>
    </div>
  );
}
